import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { DownloadAppButton } from './DownloadAppButton';
import type { Go, MarketingView } from './types';

const LINKS: { v: MarketingView; label: string }[] = [
  { v: 'features', label: 'Features' },
  { v: 'pricing', label: 'Pricing' },
  { v: 'about', label: 'About' },
];

/** Marketing-site top navigation: brand, section links, and auth CTAs.
 *  Sticky; collapses to a disclosure menu on small screens. */
export function MarketingNav({ view, go }: { view: MarketingView; go: Go }) {
  const [open, setOpen] = useState(false);

  const linkCls = (active: boolean) =>
    `rounded-md px-3 py-2 text-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
      active ? 'text-text' : 'text-muted hover:text-text'
    }`;

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-bg">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <button
          type="button"
          onClick={() => go('home')}
          className="flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label="SpecForma home"
        >
          <span className="flex size-8 items-center justify-center rounded-md bg-primary text-inverse">
            <span className="text-xs font-bold">SF</span>
          </span>
          <span className="text-lg font-semibold tracking-tight">SpecForma</span>
          <span className="rounded-full bg-primary-soft px-2 py-0.5 text-xs font-semibold text-primary">
            Beta
          </span>
        </button>

        <nav aria-label="Primary" className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.v}
              type="button"
              onClick={() => go(l.v)}
              className={linkCls(view === l.v)}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <DownloadAppButton variant="outline" size="sm">
            Download
          </DownloadAppButton>
          <Button variant="ghost" size="sm" onClick={() => go('auth')}>
            Sign in
          </Button>
          <Button size="sm" onClick={() => go('auth')}>
            Get started
          </Button>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? (
            <X className="size-5" aria-hidden="true" />
          ) : (
            <Menu className="size-5" aria-hidden="true" />
          )}
        </Button>
      </div>

      {open ? (
        <div className="border-t border-border bg-bg md:hidden">
          <nav aria-label="Primary" className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3">
            {LINKS.map((l) => (
              <button
                key={l.v}
                type="button"
                onClick={() => {
                  go(l.v);
                  setOpen(false);
                }}
                className={`text-left ${linkCls(view === l.v)}`}
              >
                {l.label}
              </button>
            ))}
            <div className="mt-2 flex flex-col gap-2">
              <DownloadAppButton variant="outline" onDone={() => setOpen(false)}>
                Download for Windows
              </DownloadAppButton>
              <Button
                variant="outline"
                onClick={() => {
                  go('auth');
                  setOpen(false);
                }}
              >
                Sign in
              </Button>
              <Button
                onClick={() => {
                  go('auth');
                  setOpen(false);
                }}
              >
                Get started
              </Button>
            </div>
          </nav>
        </div>
      ) : null}
    </header>
  );
}
